import { useViewPort } from "@website/hooks/viewport";
import { Button } from "@website/ui/button";
import { Header, Input, Main } from "@website/ui/datepicker/components";
import { FlatHeader } from "@website/ui/datepicker/components/flat-header/flat-header";
import styles from "@website/ui/datepicker/datepicker.module.scss";
import { DatepickerPropertiesType } from "@website/ui/datepicker/datepicker.types";
import {
  useCalendarLocale,
  usePopover,
  useScroll,
  useShowDate
} from "@website/ui/datepicker/hooks";
import { PopoverFull } from "@website/ui/popover-full";
import { FC, memo } from "react";

const Datepicker: FC<DatepickerPropertiesType> = (properties) => {
  const {
    id,
    acceptEmpty = false,
    type = "popover",
    error,
    fullWidth,
    helperText,
    mode,
    setMode,
    onChange,
    fromTitle,
    toTitle,
    value,
    setValue,
    changeableMode,
    calendarData,
    size,
    position,
    gapHorizontal,
    gapVertical,
    disableLimit,
    view = "default",
    suggestions,
    noBorder,
    inputClassName,
    hideMiddleLine,
    hasDropdownIcon,
    labelClassName,
    disableDates,
    isLabelTop,
    initialOpen,
    ctaAcceptClick,
    ctaCancelClick,
    backButton,
    footerContent
  } = properties;

  const isRejectable = acceptEmpty && Boolean(ctaCancelClick);

  const { onOpenPopover, onClosePopover, popoverProperties } = usePopover({
    initialOpen
  });
  const { scrollReference, setScrollReference, scrolled, scrollToTop } =
    useScroll();

  const disableCta =
    (mode === "single" && value.length === 0) ||
    (mode !== "single" && Boolean(acceptEmpty) !== true && value.length <= 1);

  const {
    calendarLocale,
    changeCalendarLocaleToGregorian,
    changeCalendarLocaleToPersian
  } = useCalendarLocale(properties);
  const { isDesktop, isMobile } = useViewPort();
  const {
    onNextMonthClick,
    onPreviousMonthClick,
    onShowTodayClick,
    showDates,
    increaseShowDatesNumber,
    nextMonthDisabled,
    previousMonthDisabled
  } = useShowDate(calendarLocale, scrollToTop, value, view);

  function onAccept() {
    onClosePopover();
    ctaAcceptClick?.();
  }

  function onClean() {
    onChange([], calendarLocale);

    if (acceptEmpty) {
      if (value?.length !== 2) ctaCancelClick?.();
    } else ctaCancelClick?.();
  }

  return (
    <div className={`${styles["datepicker"]} ${styles["rtl"]}`}>
      {type === "flat" ? (
        <>
          <FlatHeader
            calendarLocale={calendarLocale}
            nextMonthDisabled={nextMonthDisabled}
            previousMonthDisabled={previousMonthDisabled}
            scrolled={scrolled}
            showDates={
              isDesktop && view !== "mobile" ? showDates : [showDates[0]]
            }
            showNextMonth={onNextMonthClick}
            showPreviousMonth={onPreviousMonthClick}
            type={type}
            view={view}
          />
          <Main
            calendarData={calendarData}
            calendarLocale={calendarLocale}
            disableDates={disableDates}
            disableLimit={disableLimit}
            increaseShowDatesNumber={increaseShowDatesNumber}
            mode={mode}
            onChange={onChange}
            scrollReference={scrollReference}
            setScrollReference={setScrollReference}
            setValue={setValue}
            showDates={
              isDesktop && view !== "mobile" ? showDates : [showDates[0]]
            }
            suggestions={suggestions}
            type={type}
            value={value}
            view={view}
            isLabelTop={isLabelTop}
          />
        </>
      ) : (
        <>
          <Input
            calendarLocale={calendarLocale}
            changeableMode={changeableMode}
            data-testid="date_picker_input"
            error={error}
            fromTitle={fromTitle}
            fullWidth={fullWidth}
            hasDropdownIcon={hasDropdownIcon}
            helperText={helperText}
            hideMiddleLine={hideMiddleLine}
            inputClassName={inputClassName}
            labelClassName={labelClassName}
            mode={mode}
            noBorder={noBorder}
            onOpenPopover={onOpenPopover}
            setMode={setMode}
            size={size}
            toTitle={toTitle}
            value={value}
            id={id}
          />
          <PopoverFull
            {...popoverProperties}
            gapHorizontal={
              isDesktop && view !== "mobile"
                ? gapHorizontal ?? popoverProperties.gapHorizontal
                : "0px"
            }
            gapVertical={
              isDesktop && view !== "mobile"
                ? gapVertical ?? popoverProperties.gapVertical
                : "0px"
            }
            position={position ?? popoverProperties.position}
          >
            <div className={styles["popover"]}>
              <Header
                backButton={backButton}
                calendarLocale={calendarLocale}
                changeCalendarLocale={
                  calendarLocale === "persian"
                    ? changeCalendarLocaleToGregorian
                    : changeCalendarLocaleToPersian
                }
                inputProperties={{
                  fromTitle,
                  toTitle,
                  mode,
                  setMode,
                  value,
                  calendarLocale,
                  changeableMode
                }}
                nextMonthDisabled={nextMonthDisabled}
                onClose={onClosePopover}
                previousMonthDisabled={previousMonthDisabled}
                scrolled={scrolled}
                showDates={showDates}
                showNextMonth={onNextMonthClick}
                showPreviousMonth={onPreviousMonthClick}
                showToday={onShowTodayClick}
                type={type}
                view={view}
              />
              <Main
                calendarData={calendarData}
                calendarLocale={calendarLocale}
                disableDates={disableDates}
                disableLimit={disableLimit}
                increaseShowDatesNumber={increaseShowDatesNumber}
                mode={mode}
                onChange={onChange}
                scrollReference={scrollReference}
                setScrollReference={setScrollReference}
                setValue={setValue}
                showDates={showDates}
                suggestions={suggestions}
                type={type}
                value={value}
                view={view}
                isLabelTop={isLabelTop}
              />
              <div className="border-t-outline-thin flex w-full items-center justify-end border-t px-6 pb-6 pt-4 max-md:flex-col">
                {footerContent}
                <div className="flex justify-end gap-4 max-md:w-full">
                  <Button
                    onClick={onClean}
                    color="secondary"
                    size="large"
                    variant={isMobile && footerContent ? "outline" : "text"}
                    fullWidth={isMobile || view === "mobile"}
                    disabled={acceptEmpty ? !acceptEmpty : disableCta}
                  >
                    {isRejectable && properties?.value?.length !== 2
                      ? "رد کردن"
                      : "پاک کردن"}
                  </Button>
                  <Button
                    onClick={onAccept}
                    color="secondary"
                    size="large"
                    variant="contained"
                    fullWidth={isMobile || view === "mobile"}
                    disabled={disableCta}
                  >
                    تایید
                  </Button>
                </div>
              </div>
            </div>
          </PopoverFull>
        </>
      )}
    </div>
  );
};

const DatepickerMemoized = memo(Datepicker);
export { DatepickerMemoized as Datepicker };
